import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Card,
  Typography,
  Form,
  Input,
  InputNumber,
  Slider,
  Select,
  Button,
  Space,
  Row,
  Col,
  message,
  Alert,
  Tag,
  Segmented,
  Tooltip,
  Popover,
  Collapse,
} from "antd";
import {
  CloseOutlined,
  EditOutlined,
  RocketOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import AIService from "../services/AIService";
import PromptEditorModal, { PromptEditorButton } from "./PromptEditorModal";
import PromptLibraryModal, { PromptLibraryButton } from "./PromptLibraryModal";
import { getPromptBlock, getOverriddenKeys } from "../services/promptStore";
import { getSectionOverride, setSectionOverride } from "../services/sectionOverridesStore";
import { showErrorWithCopy } from "../utils/errorNotice.jsx";
import { recordTiming, recordError } from "../services/modelStats";
import { stitchVideoSegments } from "../services/videoStitcher";
import { emitGenerationComplete } from "../services/generationNotifications";
import {
  formatCooldownSeconds,
  getImageGenerationCooldownRemainingMs,
  startImageGenerationCooldown,
} from "../services/imageGenerationCooldown";
import {
  DEFAULT_IMAGE_MODEL,
  DEFAULT_VARIANT_MODEL,
  DEFAULT_VIDEO_MODEL,
  OPENAI_IMAGE_MODEL,
  normalizeModelSelection,
} from "../services/modelCatalog";
const { Text } = Typography;
const MAX_VARIANT_QUANTITY = 20;
const MAX_PASTED_REFERENCE_IMAGES = 4;
const IMAGE_RATIO_OPTIONS = [
  { value: "1:1", w: 16, h: 16 },
  { value: "4:5", w: 16, h: 20 },
  { value: "3:4", w: 16, h: 21 },
  { value: "16:9", w: 20, h: 11 },
  { value: "9:16", w: 11, h: 20 },
];
const IMAGE_RATIO_VALUES = IMAGE_RATIO_OPTIONS.map((ratio) => ratio.value);
const SCENE_MODE_OPTIONS = [{ value: "ad", label: "Ad" }];
// SCENE_MODE_COPY is now read from the prompt store (editable via Prompt Library)
const getSceneModeCopy = () => ({
  ad: getPromptBlock("scene_mode_ad"),
  product: getPromptBlock("scene_mode_product"),
  review: getPromptBlock("scene_mode_review"),
  custom: getPromptBlock("scene_mode_custom"),
});
const ANALYSIS_TEXT_DIRECTION_FIELDS = [
  { key: "visual_elements.text_overlays", label: "Headline / Text on Image" },
  { key: "messaging.value_proposition", label: "Value Proposition" },
  { key: "messaging.cta", label: "CTA Text" },
  { key: "visual_elements.primary_hook", label: "Primary Visual Hook" },
  { key: "visual_elements.composition", label: "Composition" },
  { key: "visual_elements.key_visuals", label: "Key Visuals" },
  { key: "emotional_response.mood", label: "Mood / Tone" },
  { key: "target_audience.audience_segment", label: "Target Audience" },
  {
    key: "target_audience.psychographic_profile",
    label: "Psychographic Profile",
  },
  { key: "why_it_works", label: "Why It Works" },
  { key: "hook", label: "Hook" },
  { key: "summary", label: "Summary" },
];

const getNestedTextValue = (obj, path) => {
  const value = String(path || "")
    .split(".")
    .reduce((acc, key) => acc?.[key], obj);
  if (Array.isArray(value)) {
    return value
      .map((entry) => String(entry || "").trim())
      .filter(Boolean)
      .join(", ");
  }
  return String(value || "").trim();
};

const normalizeGuidancePreview = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";
  const lines = trimmed.split("\n");
  if (lines.length > 1 && /:\s*$/.test(lines[0])) {
    return lines.slice(1).join("\n").trim();
  }
  return trimmed;
};
function VariantsControls({
  analysis,
  history,
  onSelectAnalysis,
  onVariantsChange,
  knowledgeDb,
  tags,
  brandLibrary,
  aiProfile,
  analysisKnowledge,
  adCopyKnowledge,
  apiKeys,
  analysisModel,
  variantModel,
  generationAspectRatio,
  generationTagIds,
  knowledgeActiveIds,
  generationSourceMode,
  generationSceneMode,
  specialInstructions,
  specialInstructionMode,
  specialInstructionNote,
  onPreferencesChange,
  qualityControlLevel = "off",
  onGenerationStatusChange,
  onPromptLoadChange,
  showCard = true,
  disabled = false,
  workspaceId,
}) {
  const [generating, setGenerating] = useState(false);
  const [genForm] = Form.useForm();
  const [selectedId, setSelectedId] = useState(null);
  const [selectedKnowledgeIds, setSelectedKnowledgeIds] = useState(
    knowledgeActiveIds || [],
  );
  const [selectedTagIds, setSelectedTagIds] = useState(generationTagIds || []);
  const [generationSourceModeLocal, setGenerationSourceModeLocal] = useState(
    () => {
      if (
        generationSourceMode === "analysis" ||
        generationSourceMode === "knowledge"
      ) {
        return generationSourceMode;
      }
      return analysis?.id || (history || []).length > 0
        ? "analysis"
        : "knowledge";
    },
  );
  const [generationSceneModeLocal, setGenerationSceneModeLocal] = useState(
    () =>
      SCENE_MODE_OPTIONS.some((option) => option.value === generationSceneMode)
        ? generationSceneMode
        : "ad",
  );
  const [aspectRatio, setAspectRatio] = useState(
    IMAGE_RATIO_VALUES.includes(generationAspectRatio)
      ? generationAspectRatio
      : "1:1",
  );
  const [videoAspectRatio, setVideoAspectRatio] = useState("16:9");
  const [videoResolution, setVideoResolution] = useState("720p");
  const [videoDuration, setVideoDuration] = useState(8);
  const [videoNegativePrompt, setVideoNegativePrompt] = useState("");
  const [imageModel, setImageModel] = useState(DEFAULT_IMAGE_MODEL);
  const [videoModel, setVideoModel] = useState(DEFAULT_VIDEO_MODEL);
  const [specialInstructionModeLocal, setSpecialInstructionModeLocal] =
    useState(specialInstructionMode || "rephrase");
  const [specialInstructionNoteLocal, setSpecialInstructionNoteLocal] =
    useState(specialInstructionNote || "");
  const [specialInstructionUndo, setSpecialInstructionUndo] = useState("");
  const [isRevisingSpecial, setIsRevisingSpecial] = useState(false);
  // Images pasted into the special-instructions field. Sent to image
  // generation as style references alongside product folder photos.
  const [pastedReferenceImages, setPastedReferenceImages] = useState([]);
  const [imageCooldownRemainingMs, setImageCooldownRemainingMs] = useState(() =>
    getImageGenerationCooldownRemainingMs(workspaceId),
  );
  const [messageApi, contextHolder] = message.useMessage();
  const cancelRef = useRef(false);
  const abortRef = useRef(null);

  // ── Prompt library ────────────────────────────────────────────────────────
  const [promptLibraryOpen, setPromptLibraryOpen] = useState(false);
  const [promptLibraryModifiedCount, setPromptLibraryModifiedCount] = useState(() => getOverriddenKeys().length);

  // ── Prompt editor ─────────────────────────────────────────────────────────
  const [variantPromptEditorOpen, setVariantPromptEditorOpen] = useState(false);
  const [variantPromptOverrides, setVariantPromptOverridesState] = useState(() => getSectionOverride("variantConcept"));
  const [imagePromptEditorOpen, setImagePromptEditorOpen] = useState(false);
  const [imagePromptOverrides, setImagePromptOverridesState] = useState(() => getSectionOverride("variantImage"));
  const [videoPromptEditorOpen, setVideoPromptEditorOpen] = useState(false);
  const [videoPromptOverrides, setVideoPromptOverridesState] = useState(() => getSectionOverride("variantVideo"));
  const [variantPromptPreview, setVariantPromptPreview] = useState("");
  const [imagePromptPreview, setImagePromptPreview] = useState("");
  const [videoPromptPreview, setVideoPromptPreview] = useState("");

  const setVariantPromptOverrides = (v) => { setVariantPromptOverridesState(v); setSectionOverride("variantConcept", v); };
  const setImagePromptOverrides = (v) => { setImagePromptOverridesState(v); setSectionOverride("variantImage", v); };
  const setVideoPromptOverrides = (v) => { setVideoPromptOverridesState(v); setSectionOverride("variantVideo", v); };
  const initSpecialInstructionsRef = useRef(false);
  const isSoraModel = String(videoModel || "")
    .toLowerCase()
    .startsWith("sora");
  const isSoraPro = String(videoModel || "")
    .toLowerCase()
    .startsWith(DEFAULT_VIDEO_MODEL);
  const resolvedVariantModel = normalizeModelSelection(
    variantModel || analysisModel,
    DEFAULT_VARIANT_MODEL,
  );
  const effectiveVariantModel = resolvedVariantModel;
  const watchedFocusArea = Form.useWatch("focusArea", genForm);
  const watchedQuantity = Form.useWatch("quantity", genForm);
  const watchedSpecialInstructions = Form.useWatch(
    "specialInstructions",
    genForm,
  );

  useEffect(() => {
    setSelectedKnowledgeIds(knowledgeActiveIds || []);
  }, [knowledgeActiveIds]);

  useEffect(() => {
    setSelectedTagIds(generationTagIds || []);
  }, [generationTagIds]);

  useEffect(() => {
    if (
      generationSourceMode === "analysis" ||
      generationSourceMode === "knowledge"
    ) {
      setGenerationSourceModeLocal(generationSourceMode);
    }
  }, [generationSourceMode]);

  useEffect(() => {
    if (
      SCENE_MODE_OPTIONS.some((option) => option.value === generationSceneMode)
    ) {
      setGenerationSceneModeLocal(generationSceneMode);
      return;
    }
    setGenerationSceneModeLocal("ad");
  }, [generationSceneMode]);

  useEffect(() => {
    setAspectRatio(
      IMAGE_RATIO_VALUES.includes(generationAspectRatio)
        ? generationAspectRatio
        : "1:1",
    );
  }, [generationAspectRatio]);

  useEffect(() => {
    setSpecialInstructionModeLocal(specialInstructionMode || "rephrase");
  }, [specialInstructionMode]);

  useEffect(() => {
    setSpecialInstructionNoteLocal(specialInstructionNote || "");
  }, [specialInstructionNote]);

  useEffect(() => {
    if (!specialInstructions) return;
    genForm.setFieldsValue({ specialInstructions });
  }, [specialInstructions, genForm]);

  useEffect(() => {
    const refreshCooldown = () =>
      setImageCooldownRemainingMs(
        getImageGenerationCooldownRemainingMs(workspaceId),
      );
    refreshCooldown();
    const timer = setInterval(refreshCooldown, 1000);
    return () => clearInterval(timer);
  }, [workspaceId]);

  const getContextWindowLimit = useCallback((modelName) => {
    const name = String(modelName || "").toLowerCase();
    if (name.startsWith("gemini")) return 1_000_000;
    if (name.startsWith("claude")) return 200_000;
    if (name.startsWith("gpt-5.4")) {
      return name.includes("mini") || name.includes("nano")
        ? 400_000
        : 1_050_000;
    }
    return 128_000;
  }, []);

  const buildGuidanceText = useCallback(() => {
    const profile = aiProfile || {};
    const lines = [];
    if (profile.instructions) {
      lines.push(`Custom instructions: ${profile.instructions}`);
    }
    if (profile.personality) {
      lines.push(`Personality & tone: ${profile.personality}`);
    }
    if (profile.mustDo) {
      lines.push(`Must-do: ${profile.mustDo}`);
    }
    if (profile.avoid) {
      lines.push(`Avoid: ${profile.avoid}`);
    }
    if (
      Array.isArray(profile.referenceUrls) &&
      profile.referenceUrls.length > 0
    ) {
      lines.push(
        `Reference URLs: ${profile.referenceUrls.join(", ")} (do not invent details if unsure).`,
      );
    }
    const normalizedTags = Array.isArray(tags)
      ? tags
          .map((tag) => ({
            name: tag?.name || "",
            description: tag?.description || "",
          }))
          .filter((tag) => tag.name)
      : [];
    if (normalizedTags.length > 0) {
      const limit = 12;
      const shown = normalizedTags.slice(0, limit);
      const summary = shown
        .map((tag) =>
          tag.description ? `${tag.name} (${tag.description})` : tag.name,
        )
        .join("; ");
      const more =
        normalizedTags.length > limit
          ? ` (+${normalizedTags.length - limit} more)`
          : "";
      lines.push(
        `User tag profile: ${summary}${more}. Use this to align tone and avoid duplicates.`,
      );
    }
    if (lines.length === 0) return "";
    return `\n\nUser preferences:\n${lines.join("\n")}\n`;
  }, [aiProfile, tags]);

  const buildSpecialInstructions = useCallback((baseValue) => {
    return String(baseValue || "").trim();
  }, []);

  const runSpecialInstructionRevision = useCallback(
    async (mode) => {
      const targetMode = String(mode || "").trim();
      if (!targetMode) {
        messageApi.info("Pick Rephrase or Expand.");
        return;
      }
      const current = String(
        genForm.getFieldValue("specialInstructions") || "",
      );
      if (!current.trim()) {
        messageApi.info("Add special instructions first.");
        return;
      }
      setIsRevisingSpecial(true);
      setSpecialInstructionModeLocal(targetMode);
      setSpecialInstructionUndo(current);
      try {
        const aiService = new AIService({
          ...apiKeys,
          aiProfile,
          analysisKnowledge,
          adCopyKnowledge,
          tagProfile: tags,
          generationAspectRatio,
          workspaceId,
        });
        const revised = await aiService.reviseSpecialInstructions({
          mode: targetMode,
          instructions: current,
          note: specialInstructionNoteLocal,
        });
        genForm.setFieldsValue({ specialInstructions: revised });
        messageApi.success("Special instructions updated.");
      } catch (err) {
        showErrorWithCopy(
          messageApi,
          "AI revision failed",
          err,
          "VariantsControls.runSpecialInstructionRevision",
        );
      } finally {
        setIsRevisingSpecial(false);
      }
    },
    [
      genForm,
      messageApi,
      specialInstructionNoteLocal,
      apiKeys,
      aiProfile,
      analysisKnowledge,
      adCopyKnowledge,
      tags,
      generationAspectRatio,
      workspaceId,
    ],
  );

  const handleUndoSpecialInstructions = useCallback(() => {
    if (!specialInstructionUndo) return;
    genForm.setFieldsValue({ specialInstructions: specialInstructionUndo });
    messageApi.info("Special instructions restored.");
  }, [genForm, messageApi, specialInstructionUndo]);

  const handleSpecialInstructionsPaste = useCallback(
    (event) => {
      const items = Array.from(event.clipboardData?.items || []);
      const imageItems = items.filter((item) =>
        String(item?.type || "").startsWith("image/"),
      );
      if (imageItems.length === 0) return;
      imageItems.forEach((item) => {
        const file = item.getAsFile();
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = String(reader.result || "");
          if (!dataUrl.startsWith("data:image/")) return;
          setPastedReferenceImages((prev) => {
            if (prev.length >= MAX_PASTED_REFERENCE_IMAGES) {
              messageApi.warning(
                `Up to ${MAX_PASTED_REFERENCE_IMAGES} pasted reference images. Remove one first.`,
              );
              return prev;
            }
            return [
              ...prev,
              {
                dataUrl,
                url: dataUrl,
                name: file.name || `Pasted reference ${prev.length + 1}`,
                mimeType: file.type || "image/png",
              },
            ];
          });
        };
        reader.readAsDataURL(file);
      });
    },
    [messageApi],
  );

  const handleRemovePastedReferenceImage = useCallback((index) => {
    setPastedReferenceImages((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const specialInstructionLabel = (
    <Space size="small">
      <span>Special Instructions</span>
      <Popover
        content={
          <Space direction="vertical" size="small" style={{ width: 300 }}>
            <Space size="small" align="center">
              <Tooltip title="Rephrase / Polish" placement="top">
                <Button
                  size="small"
                  type="text"
                  className={
                    specialInstructionModeLocal === "rephrase"
                      ? "ai-revise-btn ai-revise-btn--active"
                      : "ai-revise-btn"
                  }
                  loading={isRevisingSpecial}
                  onClick={() => runSpecialInstructionRevision("rephrase")}
                >
                  <span className="ms-icon">wand_shine</span>
                </Button>
              </Tooltip>
              <Tooltip title="Expand" placement="top">
                <Button
                  size="small"
                  type="text"
                  className={
                    specialInstructionModeLocal === "expand"
                      ? "ai-revise-btn ai-revise-btn--active"
                      : "ai-revise-btn"
                  }
                  loading={isRevisingSpecial}
                  onClick={() => runSpecialInstructionRevision("expand")}
                >
                  <span className="ms-icon">contextual_token_add</span>
                </Button>
              </Tooltip>
            </Space>
            <Input.TextArea
              rows={2}
              autoSize={{ minRows: 2, maxRows: 6 }}
              placeholder="AI revision note (optional). Example: keep it concise and action-focused."
              value={specialInstructionNoteLocal}
              onChange={(event) =>
                setSpecialInstructionNoteLocal(event.target.value)
              }
            />
            <div className="ai-revise-footer">
              <Button
                size="small"
                type="primary"
                disabled={!specialInstructionModeLocal}
                loading={isRevisingSpecial}
                onClick={() =>
                  runSpecialInstructionRevision(specialInstructionModeLocal)
                }
              >
                Submit
              </Button>
              <Text type="secondary" className="ai-revise-label">
                AI revise
              </Text>
            </div>
          </Space>
        }
        trigger="click"
        placement="top"
        overlayInnerStyle={{ backgroundColor: "#fff", color: "#111" }}
        color="#fff"
      >
        <Button type="text" size="small" icon={<EditOutlined />} />
      </Popover>
    </Space>
  );

  const videoResolutionOptions = useMemo(() => {
    if (isSoraModel) {
      const options = [{ value: "720p", label: "720p (1280x720)" }];
      if (isSoraPro) {
        options.push({
          value: "pro-high",
          label: "High (Sora 2 Pro 1792x1024)",
        });
      }
      return options;
    }
    return [
      { value: "720p", label: "720p (default)" },
      { value: "1080p", label: "1080p (8s only)" },
    ];
  }, [isSoraModel, isSoraPro]);

  const videoDurationOptions = useMemo(() => {
    return isSoraModel
      ? [
          { value: 4, label: "4s" },
          { value: 8, label: "8s" },
          { value: 12, label: "12s" },
        ]
      : [
          { value: 4, label: "4s" },
          { value: 6, label: "6s" },
          { value: 8, label: "8s" },
        ];
  }, [isSoraModel]);

  const ratioOptions = IMAGE_RATIO_OPTIONS.map((ratio) => ({
    value: ratio.value,
    label: (
      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span
          style={{
            width: ratio.w,
            height: ratio.h,
            border: "1px solid currentColor",
            borderRadius: 2,
            display: "inline-block",
          }}
        />
        <span>{ratio.value}</span>
      </span>
    ),
  }));

  const videoRatioOptions = [
    { value: "16:9", w: 20, h: 11 },
    { value: "9:16", w: 11, h: 20 },
  ].map((ratio) => ({
    value: ratio.value,
    label: (
      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span
          style={{
            width: ratio.w,
            height: ratio.h,
            border: "1px solid currentColor",
            borderRadius: 2,
            display: "inline-block",
          }}
        />
        <span>{ratio.value}</span>
      </span>
    ),
  }));

  useEffect(() => {
    if (!selectedId) {
      setSelectedId(analysis?.id || history?.[0]?.id || null);
    }
  }, [analysis?.id, history, selectedId]);

  useEffect(() => {
    onPreferencesChange?.({ generationAspectRatio: aspectRatio });
  }, [aspectRatio]);

  useEffect(() => {
    if (initSpecialInstructionsRef.current) return;
    initSpecialInstructionsRef.current = true;
    if (specialInstructions) {
      genForm.setFieldsValue({ specialInstructions });
    }
  }, [genForm, specialInstructions]);

  useEffect(() => {
    onPreferencesChange?.({ generationTagIds: selectedTagIds });
  }, [selectedTagIds]);

  useEffect(() => {
    onPreferencesChange?.({ generationSourceMode: generationSourceModeLocal });
  }, [generationSourceModeLocal]);

  useEffect(() => {
    onPreferencesChange?.({ generationSceneMode: generationSceneModeLocal });
  }, [generationSceneModeLocal]);

  useEffect(() => {
    onPreferencesChange?.({
      specialInstructions: String(watchedSpecialInstructions || ""),
    });
  }, [watchedSpecialInstructions]);

  useEffect(() => {
    onPreferencesChange?.({
      specialInstructionMode: specialInstructionModeLocal,
      specialInstructionNote: specialInstructionNoteLocal,
    });
  }, [specialInstructionModeLocal, specialInstructionNoteLocal]);

  useEffect(() => {
    if (analysis?.id) {
      setSelectedId(analysis.id);
    }
  }, [analysis?.id]);

  useEffect(() => {
    if (generationSourceModeLocal === "analysis") {
      const hasAnalysis = Boolean(analysis?.id) || (history || []).length > 0;
      if (!hasAnalysis) {
        setGenerationSourceModeLocal("knowledge");
      }
    }
  }, [generationSourceModeLocal, analysis?.id, history]);

  useEffect(() => {
    if (videoResolution !== "720p" && videoDuration !== 8) {
      setVideoDuration(8);
    }
  }, [videoResolution, videoDuration]);

  useEffect(() => {
    const allowed = videoDurationOptions.map((opt) => opt.value);
    if (!allowed.includes(videoDuration)) {
      setVideoDuration(allowed[0]);
    }
  }, [videoDuration, videoDurationOptions]);

  useEffect(() => {
    if (isSoraModel) {
      if (videoResolution === "1080p") {
        setVideoResolution("720p");
        return;
      }
      if (videoResolution === "pro-high" && !isSoraPro) {
        setVideoResolution("720p");
      }
      return;
    }
    if (videoResolution === "pro-high") {
      setVideoResolution("720p");
    }
  }, [isSoraModel, isSoraPro, videoResolution]);

  useEffect(() => {
    onPreferencesChange?.({ knowledgeActiveIds: selectedKnowledgeIds });
  }, [selectedKnowledgeIds]);

  useEffect(() => {
    const valid = selectedKnowledgeIds.filter((id) =>
      knowledgeDb.some((item) => item.id === id),
    );
    if (valid.length !== selectedKnowledgeIds.length) {
      setSelectedKnowledgeIds(valid);
    }
  }, [knowledgeDb, selectedKnowledgeIds]);

  useEffect(() => {
    if (tags?.length > 0) {
      const valid = selectedTagIds.filter((id) =>
        tags.some((tag) => tag.id === id),
      );
      if (valid.length !== selectedTagIds.length) {
        setSelectedTagIds(valid);
      }
    }
  }, [tags, selectedTagIds]);

  const selectedAnalysis =
    history?.find((item) => item.id === selectedId) || analysis || null;
  const context =
    selectedAnalysis?.masterContext || selectedAnalysis?.analysis || null;
  const analysisReferenceImages = useMemo(() => {
    const sourceUrl =
      selectedAnalysis?.previewDataUrl || selectedAnalysis?.previewUrl;
    if (
      generationSourceModeLocal !== "analysis" ||
      !sourceUrl ||
      selectedAnalysis?.type !== "image"
    ) {
      return [];
    }
    return [
      {
        id: `analysis-ref-${selectedAnalysis?.id || "current"}`,
        name: selectedAnalysis?.fileName
          ? `${selectedAnalysis.fileName} source ad`
          : "Analyzed source ad",
        url: sourceUrl,
        mimeType: "image/jpeg",
        __source: "analysis",
      },
    ];
  }, [generationSourceModeLocal, selectedAnalysis]);
  const usingAnalysisContext =
    generationSourceModeLocal === "analysis" && Boolean(context);
  const isVideoAnalysis =
    usingAnalysisContext && selectedAnalysis?.type === "video";
  const imageCooldownActive = !isVideoAnalysis && imageCooldownRemainingMs > 0;
  const imageCooldownSeconds = formatCooldownSeconds(imageCooldownRemainingMs);
  const isSegmentedVideo =
    usingAnalysisContext &&
    isVideoAnalysis &&
    Array.isArray(context?.segments) &&
    context.segments.length > 0;
  const selectedKnowledge = (knowledgeDb || []).filter((item) =>
    selectedKnowledgeIds.includes(item.id),
  );
  const selectedKnowledgeImageCount = selectedKnowledge.reduce(
    (sum, item) => sum + Number(item?.images?.length || 0),
    0,
  );
  const kbReferenceAwareImageRouting =
    !isVideoAnalysis &&
    String(imageModel || "").startsWith("gpt") &&
    selectedKnowledgeImageCount > 0;
  const sortedTagOptions = useMemo(() => {
    return (tags || [])
      .map((tag, index) => ({ ...tag, __index: index }))
      .sort((a, b) => {
        const starDelta =
          Number(Boolean(b.starred)) - Number(Boolean(a.starred));
        if (starDelta !== 0) return starDelta;
        return a.__index - b.__index;
      })
      .map((tag) => ({
        value: tag.id,
        label: tag.starred ? `★ ${tag.name}` : tag.name,
      }));
  }, [tags]);
  const selectedTags = (tags || []).filter((tag) =>
    selectedTagIds.includes(tag.id),
  );
  const previewAiService = useMemo(
    () =>
      new AIService({
        aiProfile,
        analysisKnowledge,
        adCopyKnowledge,
        tagProfile: tags,
        workspaceId,
      }),
    [aiProfile, analysisKnowledge, adCopyKnowledge, tags, workspaceId],
  );
  const injectedTextDirections = useMemo(() => {
    const buildSection = (key, label, value) => {
      const text = String(value || "").trim();
      if (!text) return null;
      return { key, label, value: text };
    };

    const activeOverride = String(variantPromptOverrides.main || "").trim();
    if (activeOverride) {
      return [
        {
          key: "override",
          label: "Concept prompt override",
          value: activeOverride,
        },
      ];
    }

    const focusArea = String(watchedFocusArea || "Visual Hook").trim() ||
      "Visual Hook";
    const estimatedQuantity = Math.max(
      1,
      Math.min(Number(watchedQuantity) || 3, MAX_VARIANT_QUANTITY),
    );
    const normalizedSceneMode = previewAiService.normalizeSceneMode(
      generationSceneModeLocal,
    );
    const sourceMode =
      generationSourceModeLocal === "analysis" && usingAnalysisContext
        ? "analysis"
        : "knowledge";
    const promptChannels = previewAiService
      .getSceneModeChannelPool(normalizedSceneMode, sourceMode, focusArea)
      .slice(0, Math.min(estimatedQuantity, 10));
    const remainingChannelRotations = Math.max(
      0,
      estimatedQuantity - promptChannels.length,
    );
    const sections = [
      buildSection(
        "brief",
        "Generation brief",
        [
          `Source mode: ${
            sourceMode === "analysis" ? "From analysis" : "Fresh creative"
          }`,
          `Focus area: ${focusArea}`,
          `Scene mode: ${normalizedSceneMode}`,
          `Media type: ${isVideoAnalysis ? "Video variants" : "Image variants"}`,
          `Quantity: ${estimatedQuantity}`,
          `Aspect ratio: ${isVideoAnalysis ? videoAspectRatio : aspectRatio}`,
        ].join("\n"),
      ),
      buildSection(
        "channels",
        "Prompt channels",
        [
          ...promptChannels.map(
            (channel, index) => `${index + 1}. ${String(channel || "").trim()}`,
          ),
          remainingChannelRotations > 0
            ? `+ ${remainingChannelRotations} more variant${
                remainingChannelRotations === 1 ? "" : "s"
              } will reuse this channel rotation.`
            : "",
        ]
          .filter(Boolean)
          .join("\n"),
      ),
    ];

    if (sourceMode === "analysis") {
      sections.push(
        buildSection(
          "analysis",
          "Analysis text anchors",
          ANALYSIS_TEXT_DIRECTION_FIELDS.map(({ key, label }) => {
            const value = getNestedTextValue(context, key);
            return value ? `${label}: ${value}` : "";
          })
            .filter(Boolean)
            .join("\n"),
        ),
      );
    } else {
      const scratchContext = previewAiService.buildScratchGenerationContext({
        knowledgeFolders: selectedKnowledge,
        tags: selectedTags,
        brandLibrary,
        focusArea,
        sceneMode: normalizedSceneMode,
        specialInstructions: watchedSpecialInstructions || "",
        aspectRatio: isVideoAnalysis ? videoAspectRatio : aspectRatio,
      });
      const selectedProducts = Array.isArray(
        scratchContext?.product_details?.selected_products,
      )
        ? scratchContext.product_details.selected_products
        : [];
      const selectedTagDirections = Array.isArray(
        scratchContext?.creative_strategy?.selected_tags,
      )
        ? scratchContext.creative_strategy.selected_tags
        : [];
      sections.push(
        buildSection(
          "brand",
          "Brand guidance",
          [
            scratchContext?.brand_identity?.name
              ? `Brand: ${scratchContext.brand_identity.name}`
              : "",
            scratchContext?.brand_identity?.message
              ? `Message: ${scratchContext.brand_identity.message}`
              : "",
            Array.isArray(scratchContext?.brand_identity?.colors) &&
            scratchContext.brand_identity.colors.length > 0
              ? `Colors: ${scratchContext.brand_identity.colors.join(", ")}`
              : "",
            Array.isArray(scratchContext?.brand_identity?.fonts) &&
            scratchContext.brand_identity.fonts.length > 0
              ? `Fonts: ${scratchContext.brand_identity.fonts.join(", ")}`
              : "",
          ]
            .filter(Boolean)
            .join("\n"),
        ),
      );
      sections.push(
        buildSection(
          "products",
          "Product folder guidance",
          selectedProducts
            .map((product, index) =>
              [
                `${index + 1}. ${product?.name || "Selected product"}`,
                product?.notes ? `Notes: ${product.notes}` : "",
                product?.reference_image_count
                  ? `Reference images: ${product.reference_image_count}`
                  : "",
              ]
                .filter(Boolean)
                .join("\n"),
            )
            .filter(Boolean)
            .join("\n\n"),
        ),
      );
      sections.push(
        buildSection(
          "tags",
          "Selected tag guidance",
          selectedTagDirections
            .map((tag, index) =>
              tag?.description
                ? `${index + 1}. ${tag.name}: ${tag.description}`
                : `${index + 1}. ${tag.name}`,
            )
            .filter(Boolean)
            .join("\n"),
        ),
      );
      sections.push(
        buildSection(
          "rules",
          "Built-in context rules",
          [
            scratchContext?.product_details?.product_image_source_rule || "",
            scratchContext?.product_details?.headline_source_rule || "",
            scratchContext?.creative_strategy?.performance_goal || "",
            scratchContext?.tone?.desired_feel
              ? `Desired feel: ${scratchContext.tone.desired_feel}`
              : "",
          ]
            .filter(Boolean)
            .join("\n"),
        ),
      );
    }

    sections.push(
      buildSection(
        "special",
        normalizedSceneMode === "custom"
          ? "Custom scene direction"
          : "Special instructions",
        buildSpecialInstructions(watchedSpecialInstructions),
      ),
    );
    sections.push(
      buildSection(
        "knowledge",
        "General knowledge",
        normalizeGuidancePreview(previewAiService.buildAnalysisGuidance()),
      ),
    );
    sections.push(
      buildSection(
        "copy-library",
        "Copy library guidance",
        normalizeGuidancePreview(previewAiService.buildAdCopyGuidance()),
      ),
    );
    sections.push(
      buildSection(
        "preferences",
        "User preferences",
        normalizeGuidancePreview(previewAiService.buildAiGuidance()),
      ),
    );

    return sections.filter(Boolean);
  }, [
    variantPromptOverrides.main,
    watchedFocusArea,
    previewAiService,
    generationSceneModeLocal,
    generationSourceModeLocal,
    usingAnalysisContext,
    watchedQuantity,
    isVideoAnalysis,
    videoAspectRatio,
    aspectRatio,
    context,
    selectedKnowledge,
    selectedTags,
    brandLibrary,
    watchedSpecialInstructions,
    buildSpecialInstructions,
  ]);
  const contextWindowUsage = useMemo(() => {
    const overloadThresholdPercent = 80;
    const quantity = Math.max(
      1,
      Math.min(Number(watchedQuantity) || 3, MAX_VARIANT_QUANTITY),
    );
    const focusArea = watchedFocusArea || "Visual Hook";
    const scratchAddonActive = /\bmanmade\b|\bman made\b/i.test(
      [
        brandLibrary?.name,
        brandLibrary?.message,
        ...selectedKnowledge.flatMap((item) => [item?.name, item?.notes]),
      ]
        .map((value) => String(value || ""))
        .join(" "),
    );
    const scratchContextPreview = JSON.stringify(
      {
        source_of_truth: "knowledge_base_only",
        brand_identity: {
          name: brandLibrary?.name || "",
          message: brandLibrary?.message || "",
          colors: brandLibrary?.colors || [],
        },
        visual_style: {
          aspect_ratio: isVideoAnalysis ? videoAspectRatio : aspectRatio,
          scene_mode: generationSceneModeLocal,
          focus_area: focusArea,
          creativity: Number(genForm.getFieldValue("creativity")) || 0.7,
        },
        product_details: selectedKnowledge.map((item) => ({
          name: item?.name || "",
          notes: item?.notes || "",
          reference_image_count: Array.isArray(item?.images)
            ? item.images.length
            : 0,
        })),
        creative_strategy: {
          selected_tags: selectedTags.map((tag) => ({
            name: tag?.name || "",
            description: tag?.description || "",
          })),
          special_instructions: watchedSpecialInstructions || "",
        },
        knowledge_layer_addon: scratchAddonActive
          ? "manmade_book_brain_addon"
          : "",
      },
      null,
      2,
    );
    const prompt = usingAnalysisContext
      ? `You are an expert creative strategist. Using the source JSON context below, generate ${quantity} distinct variant contexts.
Rules:
- Keep the exact same JSON schema as the source.
- Modify values to emphasize the focus area: ${focusArea}.
- Keep changes realistic and consistent.
- Preserve or increase the level of detail in every field. Do not shorten or generalize.
- Return ONLY a JSON object with this shape:
  { "variants": [ { "rationale": "...", "context": { ...same schema... } } ] }

Source JSON:
${JSON.stringify(context || {})}

Scene style preference:
Mode: ${generationSceneModeLocal}
${getSceneModeCopy()[generationSceneModeLocal] || getSceneModeCopy().ad}${buildGuidanceText()}`
      : `You are a world-class direct response creative strategist building static ad concepts from scratch.
Use the CONTEXT JSON as the only source of truth for product identity, tags, settings, brand info, and directions.

CONTEXT JSON:
${scratchContextPreview}

PRIMARY TASK:
Generate ${quantity} fresh static ad concepts for image creation from scratch.
Focus area: ${focusArea}.
Scene style preference:
Mode: ${generationSceneModeLocal}
${getSceneModeCopy()[generationSceneModeLocal] || getSceneModeCopy().ad}
Return strict JSON matching the internal variants schema.${buildGuidanceText()}`;
    const estimatedTokens = Math.max(0, Math.ceil(prompt.length / 4));
    const maxTokens = getContextWindowLimit(effectiveVariantModel);
    const usedPercent = maxTokens
      ? Math.min(100, (estimatedTokens / maxTokens) * 100)
      : 0;
    const thresholdFillPercent = Math.min(
      100,
      (usedPercent / overloadThresholdPercent) * 100,
    );
    return {
      model: effectiveVariantModel,
      estimatedTokens,
      maxTokens,
      usedPercent,
      overloadThresholdPercent,
      thresholdFillPercent,
      isNearThreshold: usedPercent >= overloadThresholdPercent * 0.85,
      isOverThreshold: usedPercent >= overloadThresholdPercent,
    };
  }, [
    effectiveVariantModel,
    buildGuidanceText,
    context,
    getContextWindowLimit,
    usingAnalysisContext,
    selectedKnowledge,
    selectedTags,
    brandLibrary,
    generationSceneModeLocal,
    aspectRatio,
    videoAspectRatio,
    isVideoAnalysis,
    genForm,
    watchedFocusArea,
    watchedQuantity,
    watchedSpecialInstructions,
  ]);

  useEffect(() => {
    onPromptLoadChange?.(contextWindowUsage);
  }, [contextWindowUsage, onPromptLoadChange]);

  const buildPreviewMediaPromptData = useCallback(() => {
    const formValues = genForm.getFieldsValue();
    const focusArea = String(formValues.focusArea || "Visual Hook").trim() ||
      "Visual Hook";
    const special = String(formValues.specialInstructions || "").trim();
    const sourceMode =
      generationSourceModeLocal === "analysis" && usingAnalysisContext
        ? "analysis"
        : "knowledge";
    const normalizedSceneMode = previewAiService.normalizeSceneMode(
      generationSceneModeLocal,
    );
    const promptChannel =
      previewAiService.getSceneModeChannelPool(
        normalizedSceneMode,
        sourceMode,
        focusArea,
      )[0] || "";
    const productFocus = selectedKnowledge
      .map((item) => String(item?.name || "").trim())
      .filter(Boolean)
      .join(", ");
    const productNotes = selectedKnowledge
      .map((item) => String(item?.notes || "").trim())
      .filter(Boolean)
      .join(" | ");
    const previewContext =
      usingAnalysisContext && context
        ? previewAiService.normalizeKnowledgeVariantContext(context, {
            specialInstructions: special,
            promptChannel,
            productFocus,
            productNotes,
            focusArea,
          })
        : previewAiService.normalizeKnowledgeVariantContext(
            {
              product_focus: productFocus,
              product_notes: productNotes,
              promptChannel,
              focusArea,
              visual_elements: {
                primary_hook: promptChannel || focusArea,
                composition:
                  normalizedSceneMode === "review"
                    ? "authentic review-style product-in-context shot"
                    : normalizedSceneMode === "product"
                      ? "premium product-forward studio composition"
                      : "clear paid-social ad composition with strong text hierarchy",
                key_visuals: selectedTags
                  .map((tag) => String(tag?.name || "").trim())
                  .filter(Boolean)
                  .slice(0, 4),
                lighting_style:
                  normalizedSceneMode === "review"
                    ? "natural lived-in light"
                    : "clean commercial lighting",
                lighting_direction: "clear key light with readable contrast",
                color_palette: (Array.isArray(brandLibrary?.colors)
                  ? brandLibrary.colors
                  : []
                )
                  .map((color) => String(color || "").trim())
                  .filter(Boolean)
                  .slice(0, 4),
                text_overlays: "",
              },
              messaging: {
                value_proposition: "",
                cta: "",
              },
              environment: {
                setting:
                  normalizedSceneMode === "review"
                    ? "real lived-in environment"
                    : normalizedSceneMode === "product"
                      ? "clean studio surface"
                      : "brand-led ad environment",
                backdrop:
                  normalizedSceneMode === "product"
                    ? "premium minimal backdrop"
                    : "high-contrast branded backdrop",
                props: [],
              },
              emotional_response: {
                mood: "",
              },
              target_audience: {
                audience_segment:
                  selectedTags
                    .map((tag) => String(tag?.name || "").trim())
                    .filter(Boolean)
                    .join(", ") || "selected product audience",
              },
            },
            {
              specialInstructions: special,
              promptChannel,
              productFocus,
              productNotes,
              focusArea,
            },
          );
    const previewKnowledgePayload = {
      name: productFocus,
      notes: selectedKnowledge
        .map((item) => String(item?.notes || "").trim())
        .filter(Boolean)
        .join("\n"),
      sourceMode,
      strictKnowledgeLock: selectedKnowledge.length > 0,
      knowledgeFolders: selectedKnowledge,
      images: selectedKnowledge.flatMap((item) => item.images || []),
      referenceImages: selectedKnowledge.flatMap((item) => item.images || []),
      analysisReferenceImages,
      styleReferenceImages: pastedReferenceImages,
      tags: selectedTags,
      focusArea,
      sceneMode: generationSceneModeLocal,
      creativity: formValues.creativity,
      aspectRatio: isVideoAnalysis ? videoAspectRatio : aspectRatio,
      specialInstructions: special,
      brand: brandLibrary,
      generationSettings: {
        focusArea,
        sceneMode: generationSceneModeLocal,
        creativity: formValues.creativity,
        aspectRatio: isVideoAnalysis ? videoAspectRatio : aspectRatio,
        promptChannel,
      },
      imageModel,
      videoStyle:
        "Photoreal live-action realism with grounded movement and stable continuity.",
      actorProfile: context?.actor_profile || {},
      continuityLocks: context?.continuity_locks || {},
      videoConfig: {
        model: videoModel,
        aspectRatio: videoAspectRatio,
        resolution: videoResolution,
        durationSeconds: videoDuration,
        negativePrompt: videoNegativePrompt,
      },
    };
    const previewVideoContext =
      isVideoAnalysis &&
      Array.isArray(context?.segments) &&
      context.segments.length > 0
        ? {
            ...context.segments[0],
            actor_profile: context?.actor_profile || {},
            continuity_locks: context?.continuity_locks || {},
          }
        : previewContext;
    return {
      previewContext,
      previewVideoContext,
      previewKnowledgePayload,
    };
  }, [
    genForm,
    generationSourceModeLocal,
    usingAnalysisContext,
    previewAiService,
    generationSceneModeLocal,
    selectedKnowledge,
    selectedTags,
    brandLibrary,
    analysisReferenceImages,
    pastedReferenceImages,
    isVideoAnalysis,
    videoAspectRatio,
    aspectRatio,
    imageModel,
    videoModel,
    videoResolution,
    videoDuration,
    videoNegativePrompt,
    context,
  ]);

  const handleOpenVariantPromptEditor = async () => {
    const aiService = new AIService({ workspaceId });
    const formValues = genForm.getFieldsValue();
    try {
      let result;
      if (usingAnalysisContext && context) {
        result = await aiService.generateVariants(context, {
          ...formValues,
          model: effectiveVariantModel,
          sceneMode: generationSceneModeLocal,
          specialInstructions: formValues.specialInstructions || "",
          _previewOnly: true,
        });
      } else {
        result = await aiService.generateKnowledgeBasedVariants({
          ...formValues,
          model: effectiveVariantModel,
          sceneMode: generationSceneModeLocal,
          specialInstructions: formValues.specialInstructions || "",
          knowledgeFolders: selectedKnowledge,
          tags: selectedTags,
          brandLibrary,
          aspectRatio,
          _previewOnly: true,
        });
      }
      setVariantPromptPreview(result?.prompt || "");
    } catch {
      setVariantPromptPreview("");
    }
    setVariantPromptEditorOpen(true);
  };

  const handleOpenImagePromptEditor = useCallback(() => {
    try {
      const { previewContext, previewKnowledgePayload } =
        buildPreviewMediaPromptData();
      const prompt = previewAiService.buildImagePrompt(
        previewContext,
        previewKnowledgePayload,
      );
      setImagePromptPreview(String(prompt || ""));
    } catch {
      setImagePromptPreview("");
    }
    setImagePromptEditorOpen(true);
  }, [buildPreviewMediaPromptData, previewAiService]);

  const handleOpenVideoPromptEditor = useCallback(() => {
    try {
      const { previewVideoContext, previewKnowledgePayload } =
        buildPreviewMediaPromptData();
      const prompt = previewAiService.buildVideoPrompt(
        previewVideoContext,
        previewKnowledgePayload,
      );
      setVideoPromptPreview(String(prompt || ""));
    } catch {
      setVideoPromptPreview("");
    }
    setVideoPromptEditorOpen(true);
  }, [buildPreviewMediaPromptData, previewAiService]);

  const handleGenerate = async () => {
    if (generationSourceModeLocal === "analysis" && !context) {
      messageApi.error("No analysis available. Upload and analyze a creative.");
      return;
    }

    if (selectedKnowledge.length === 0) {
      messageApi.error("Select a product folder in Settings for generation.");
      return;
    }

    if (!isVideoAnalysis) {
      const remainingMs = getImageGenerationCooldownRemainingMs(workspaceId);
      if (remainingMs > 0) {
        setImageCooldownRemainingMs(remainingMs);
        messageApi.warning(
          `Image generation cooldown active. Try again in ${formatCooldownSeconds(
            remainingMs,
          )}s.`,
        );
        return;
      }
    }

    cancelRef.current = false;
    const controller = new AbortController();
    abortRef.current = controller;
    const isCancelled = () => cancelRef.current || controller.signal.aborted;
    const throwIfCancelled = () => {
      if (isCancelled()) {
        const err = new Error("Generation cancelled");
        err.name = "AbortError";
        throw err;
      }
    };

    setGenerating(true);
    onGenerationStatusChange?.({
      stage: "variants",
      total: 0,
      completed: 0,
      message:
        generationSourceModeLocal === "analysis"
          ? "Generating variants..."
          : "Building fresh concepts from knowledge...",
      startedAt: Date.now(),
    });
    const variantsStart = performance.now();
    let runCompleted = false;
    let runCancelled = false;
    let fatalErrorMessage = "";
    let hadItemFailures = false;
    try {
      const aiService = new AIService({
        ...apiKeys,
        aiProfile,
        analysisKnowledge,
        adCopyKnowledge,
        tagProfile: tags,
        generationAspectRatio,
        workspaceId,
      });

      const formValues = genForm.getFieldsValue();
      const revisedSpecialInstructions = buildSpecialInstructions(
        formValues.specialInstructions,
      );
      const desiredCount = Math.max(
        1,
        Math.min(formValues.quantity || 1, MAX_VARIANT_QUANTITY),
      );
      const usingAnalysisMode = generationSourceModeLocal === "analysis";
      const placeholders = Array.from({ length: desiredCount }, (_, index) => ({
        id: `pending-${Date.now()}-${index}`,
        title: usingAnalysisMode
          ? `Variant ${index + 1}`
          : `Fresh Concept ${index + 1}`,
        rationale: "",
        context: null,
        imageStatus: "pending",
        mediaType: isVideoAnalysis ? "video" : "image",
        placeholder: true,
      }));
      onVariantsChange(placeholders);
      throwIfCancelled();

      if (isSegmentedVideo) {
        let actorReferenceImages = [];
        try {
          actorReferenceImages =
            await aiService.buildVideoContinuityReferenceImages(
              selectedAnalysis?.previewUrl || "",
              context?.segments || [],
              controller.signal,
              { maxImages: 3 },
            );
        } catch (error) {
          actorReferenceImages = [];
        }
        const productReferenceImages = selectedKnowledge.flatMap(
          (item) => item.images || [],
        );
        const sharedReferenceImages = [
          ...actorReferenceImages,
          ...productReferenceImages,
        ];
        const nextVariants = await aiService.generateSegmentedVariants(
          context,
          {
            ...formValues,
            model: effectiveVariantModel,
            videoModel,
            videoResolution,
            referenceImages: sharedReferenceImages,
          },
        );
        throwIfCancelled();
        onGenerationStatusChange?.((prev) => ({
          ...prev,
          stage: "variants",
          message: "Variants ready",
          forcePercent: 100,
        }));
        await new Promise((resolve) => setTimeout(resolve, 180));
        recordTiming({
          category: "variants",
          model: effectiveVariantModel,
          ms: performance.now() - variantsStart,
          ok: true,
        });

        const seeded = nextVariants.map((item) => ({
          ...item,
          imageStatus: "pending",
          mediaType: "video",
          generationModel: effectiveVariantModel,
          imageModel: videoModel,
          segments: (item.segments || []).map((seg) => ({
            ...seg,
            status: "pending",
            videoUrl: "",
            mimeType: "",
            error: "",
          })),
        }));
        onVariantsChange(seeded);
        const sceneJobs = seeded.flatMap((variant) =>
          (variant.segments || []).map((segment, segmentIndex) => ({
            variantId: variant.id,
            variant,
            segment,
            segmentIndex,
          })),
        );
        const totalWorkUnits = sceneJobs.length + seeded.length;
        onGenerationStatusChange?.({
          stage: "images",
          total: totalWorkUnits,
          completed: 0,
          message: "Rendering video scenes in parallel...",
          startedAt: Date.now(),
          forcePercent: null,
        });
        messageApi.success("Variants generated. Rendering scene batches...");

        const shouldRunQc = qualityControlLevel !== "off";
        const maxQcRegenerations =
          qualityControlLevel === "strict"
            ? 2
            : qualityControlLevel === "basic"
              ? 1
              : 0;

        const updateVariant = (variantId, updater) => {
          if (isCancelled()) return;
          onVariantsChange((prev) =>
            prev.map((item) => (item.id === variantId ? updater(item) : item)),
          );
        };
        const advanceProgress = (message) => {
          if (isCancelled()) return;
          onGenerationStatusChange?.((prev) => ({
            ...prev,
            stage: "images",
            message: message || prev?.message || "Rendering video scenes...",
            completed: (prev?.completed || 0) + 1,
          }));
        };
        const variantState = new Map(
          seeded.map((variant) => [
            variant.id,
            {
              failed: false,
              segmentUrls: new Array((variant.segments || []).length).fill(""),
            },
          ]),
        );
        const baseVideoKnowledgePayload = {
          name: selectedKnowledge.map((item) => item.name).join(", "),
          notes: selectedKnowledge
            .map((item) => item.notes)
            .filter(Boolean)
            .join("\n"),
          sourceMode: "analysis",
          strictKnowledgeLock: false,
          knowledgeFolders: selectedKnowledge,
          images: productReferenceImages,
          referenceImages: sharedReferenceImages,
          actorReferenceImages,
          tags: selectedTags,
          focusArea: formValues.focusArea || "",
          sceneMode: generationSceneModeLocal,
          creativity: formValues.creativity,
          aspectRatio: videoAspectRatio,
          brand: brandLibrary,
          videoStyle:
            "Photoreal live-action realism with grounded movement and stable continuity.",
          actorProfile: context?.actor_profile || {},
          continuityLocks: context?.continuity_locks || {},
        };
        const buildSceneKnowledgePayload = (
          variant,
          segment,
          retryFeedback = "",
        ) => ({
          ...baseVideoKnowledgePayload,
          promptChannel: variant.promptChannel || segment.renderChannel || "",
          specialInstructions: retryFeedback
            ? `${revisedSpecialInstructions || ""}\nQC retry note: ${retryFeedback}`.trim()
            : revisedSpecialInstructions,
          generationSettings: {
            focusArea: formValues.focusArea || "",
            sceneMode: generationSceneModeLocal,
            creativity: formValues.creativity,
            aspectRatio: videoAspectRatio,
            promptChannel: variant.promptChannel || segment.renderChannel || "",
          },
          videoConfig: {
            model: videoModel,
            aspectRatio: videoAspectRatio,
            resolution: videoResolution,
            durationSeconds: Number(segment.durationSec) || 8,
            negativePrompt: videoNegativePrompt,
          },
        });
        const renderSceneJob = async (job) => {
          const state = variantState.get(job.variantId);
          if (!state) {
            advanceProgress("Rendering video scenes in parallel...");
            return;
          }
          if (state.failed) {
            advanceProgress("Skipping remaining scenes for failed variant...");
            return;
          }
          throwIfCancelled();
          updateVariant(job.variantId, (item) => ({
            ...item,
            imageStatus: "generating",
            imageError: "",
            segments: item.segments.map((seg, segIndex) =>
              segIndex === job.segmentIndex
                ? { ...seg, status: "generating", error: "" }
                : seg,
            ),
          }));

          const segmentContext = {
            ...job.segment,
            actor_profile: context?.actor_profile || {},
            continuity_locks: context?.continuity_locks || {},
          };

          let mediaResult = null;
          let qcResult = null;
          let finalQcFailure = null;
          let retryFeedback = "";
          const attemptLimit = shouldRunQc ? maxQcRegenerations + 1 : 1;
          const mediaStart = performance.now();

          try {
            for (let attempt = 0; attempt < attemptLimit; attempt += 1) {
              throwIfCancelled();
              const knowledgePayload = buildSceneKnowledgePayload(
                job.variant,
                job.segment,
                retryFeedback,
              );
              mediaResult = await aiService.generateVideoFromContext(
                segmentContext,
                knowledgePayload,
                controller.signal,
                videoPromptOverrides.main || "",
              );
              if (!shouldRunQc) {
                break;
              }
              throwIfCancelled();
              onGenerationStatusChange?.((prev) => ({
                ...prev,
                stage: "images",
                message: `Running QC (${qualityControlLevel})...`,
              }));
              qcResult = await aiService.runCreativeQualityControl({
                mediaUrl: mediaResult.videoUrl,
                mediaType: "video",
                mimeType: mediaResult.mimeType,
                context: segmentContext,
                qcLevel: qualityControlLevel,
                expectedAspectRatio: videoAspectRatio,
                referenceImages: productReferenceImages,
              });
              if (qcResult?.pass) {
                finalQcFailure = null;
                break;
              }
              finalQcFailure = qcResult;
              const canRetry = attempt < attemptLimit - 1;
              if (!canRetry) {
                break;
              }
              const failed = Array.isArray(qcResult?.failingCriteria)
                ? qcResult.failingCriteria.join(", ")
                : "quality criteria";
              retryFeedback = `Regenerate with the same JSON/request. Fix failing criteria: ${failed}.`;
              onGenerationStatusChange?.((prev) => ({
                ...prev,
                stage: "images",
                message: "Scene sent back and regenerating.",
              }));
            }

            if (shouldRunQc && finalQcFailure && !qcResult?.pass) {
              const issueLine =
                finalQcFailure?.issues?.slice(0, 2)?.join("; ") ||
                finalQcFailure?.summary ||
                "Creative failed quality checks.";
              throw new Error(`QC failed: ${issueLine}`);
            }

            recordTiming({
              category: "videos",
              model: videoModel,
              ms: performance.now() - mediaStart,
              ok: true,
            });
            state.segmentUrls[job.segmentIndex] = mediaResult.videoUrl;
            updateVariant(job.variantId, (item) => ({
              ...item,
              segments: item.segments.map((seg, segIndex) =>
                segIndex === job.segmentIndex
                  ? {
                      ...seg,
                      status: "done",
                      videoUrl: mediaResult.videoUrl,
                      mimeType: mediaResult.mimeType,
                      blob: mediaResult.blob || null,
                      qcResult,
                    }
                  : seg,
              ),
            }));
          } catch (err) {
            if (isCancelled() || err?.name === "AbortError") {
              throw err;
            }
            state.failed = true;
            hadItemFailures = true;
            recordTiming({
              category: "videos",
              model: videoModel,
              ms: performance.now() - mediaStart,
              ok: false,
            });
            recordError({ category: "videos", model: videoModel });
            updateVariant(job.variantId, (item) => ({
              ...item,
              imageStatus: "error",
              imageError: err.message,
              segments: item.segments.map((seg, segIndex) =>
                segIndex === job.segmentIndex
                  ? {
                      ...seg,
                      status: "error",
                      error: err.message,
                    }
                  : seg,
              ),
            }));
          } finally {
            advanceProgress("Rendering video scenes in parallel...");
          }
        };

        const maxSceneWorkers = String(videoModel || "")
          .toLowerCase()
          .startsWith("sora")
          ? 2
          : 4;
        let nextSceneJobIndex = 0;
        const claimNextSceneJob = () => {
          if (nextSceneJobIndex >= sceneJobs.length) return null;
          const job = sceneJobs[nextSceneJobIndex];
          nextSceneJobIndex += 1;
          return job;
        };
        const workers = Array.from(
          {
            length: Math.max(
              1,
              Math.min(maxSceneWorkers, sceneJobs.length || 1),
            ),
          },
          () =>
            (async () => {
              while (true) {
                const job = claimNextSceneJob();
                if (!job) return;
                await renderSceneJob(job);
              }
            })(),
        );
        await Promise.all(workers);
        throwIfCancelled();

        onGenerationStatusChange?.((prev) => ({
          ...prev,
          stage: "images",
          message: "Stitching video variants...",
        }));

        const stitchedVariantIds = [];
        for (const variant of seeded) {
          const state = variantState.get(variant.id);
          if (!state || state.failed) {
            advanceProgress("Skipping stitch for failed variant...");
            continue;
          }
          try {
            const readyUrls = state.segmentUrls.filter(Boolean);
            if (readyUrls.length !== variant.segments.length) {
              throw new Error(
                "Some scenes are missing and could not be stitched.",
              );
            }
            const stitched = await stitchVideoSegments(state.segmentUrls);
            throwIfCancelled();
            updateVariant(variant.id, (item) => ({
              ...item,
              imageStatus: "done",
              stitchedVideoUrl: stitched.url,
              stitchedMimeType: "video/mp4",
              videoUrl: stitched.url,
              mimeType: "video/mp4",
              blob: stitched.blob || null,
            }));
            stitchedVariantIds.push(variant.id);
          } catch (err) {
            if (isCancelled() || err?.name === "AbortError") {
              throw err;
            }
            hadItemFailures = true;
            updateVariant(variant.id, (item) => ({
              ...item,
              imageStatus: "error",
              imageError: err.message,
            }));
          } finally {
            advanceProgress("Stitching video variants...");
          }
        }

        if (stitchedVariantIds.length > 0 && !isCancelled()) {
          onGenerationStatusChange?.((prev) => ({
            ...prev,
            stage: "images",
            message: "Running neuro brain-scan analysis...",
          }));
          for (const variantId of stitchedVariantIds) {
            if (isCancelled()) break;
            const variant = seeded.find((v) => v.id === variantId);
            if (!variant) continue;
            try {
              const neuroReport = await aiService.generateVideoNeuroReport(
                context,
                variant.segments || [],
                { model: effectiveVariantModel },
              );
              throwIfCancelled();
              updateVariant(variantId, (item) => ({
                ...item,
                neuroReport,
              }));
              advanceProgress("Running neuro brain-scan analysis...");
            } catch (neuroErr) {
              if (isCancelled() || neuroErr?.name === "AbortError") break;
              updateVariant(variantId, (item) => ({
                ...item,
                neuroReport: null,
                neuroReportError: neuroErr?.message || "Neuro analysis failed",
              }));
              advanceProgress(
                "Neuro analysis failed for variant, continuing...",
              );
            }
          }
        }

        runCompleted = true;
        setGenerating(false);
        onGenerationStatusChange?.((prev) => ({
          ...prev,
          stage: "idle",
          message: "",
        }));
        return;
      }
      const nextVariants = usingAnalysisMode
        ? await aiService.generateVariants(context, {
            ...formValues,
            model: effectiveVariantModel,
            sceneMode: generationSceneModeLocal,
            specialInstructions: revisedSpecialInstructions,
            promptOverride: variantPromptOverrides.main || "",
          })
        : await aiService.generateKnowledgeBasedVariants({
            ...formValues,
            model: effectiveVariantModel,
            sceneMode: generationSceneModeLocal,
            specialInstructions: revisedSpecialInstructions,
            knowledgeFolders: selectedKnowledge,
            tags: selectedTags,
            brandLibrary,
            aspectRatio: isVideoAnalysis ? videoAspectRatio : aspectRatio,
            promptOverride: variantPromptOverrides.main || "",
          });
      onGenerationStatusChange?.((prev) => ({
        ...prev,
        stage: "variants",
        message: usingAnalysisMode ? "Variants ready" : "Fresh concepts ready",
        forcePercent: 100,
      }));
      await new Promise((resolve) => setTimeout(resolve, 180));
      const variantsMs = performance.now() - variantsStart;
      recordTiming({
        category: "variants",
        model: effectiveVariantModel,
        ms: variantsMs,
        ok: true,
      });
      const seeded = nextVariants.map((item) => ({
        ...item,
        imageStatus: "pending",
        mediaType: isVideoAnalysis ? "video" : "image",
        generationModel: effectiveVariantModel,
        imageModel: isVideoAnalysis ? videoModel : imageModel,
      }));
      onVariantsChange(seeded);
      onGenerationStatusChange?.({
        stage: "images",
        total: seeded.length,
        completed: 0,
        message: isVideoAnalysis
          ? "Generating videos..."
          : usingAnalysisMode
            ? "Generating images..."
            : "Generating fresh creatives...",
        startedAt: Date.now(),
        forcePercent: null,
      });
      messageApi.success(
        isVideoAnalysis
          ? "Variants generated. Creating videos..."
          : usingAnalysisMode
            ? "Variants generated. Creating images..."
            : "Fresh concepts generated. Creating images...",
      );
      if (!isVideoAnalysis) {
        startImageGenerationCooldown(workspaceId);
        setImageCooldownRemainingMs(
          getImageGenerationCooldownRemainingMs(workspaceId),
        );
      }

      const maxPromptChannels = 10;
      const channelCount = Math.min(maxPromptChannels, seeded.length);
      const channelQueues = Array.from({ length: channelCount }, () => []);
      for (let index = 0; index < seeded.length; index += 1) {
        const channelIndex = index % channelCount;
        const promptNumber = Math.floor(index / channelCount) + 1;
        channelQueues[channelIndex].push({
          variant: seeded[index],
          channelIndex: channelIndex + 1,
          promptNumber,
        });
      }
      const shouldRunQc = qualityControlLevel !== "off";
      const maxQcRegenerations =
        qualityControlLevel === "strict"
          ? 2
          : qualityControlLevel === "basic"
            ? 1
            : 0;
      const workers = channelQueues.map((channelQueue) =>
        (async () => {
          for (const job of channelQueue) {
            if (isCancelled()) return;
            const { variant } = job;
            try {
              if (isCancelled()) return;
              onVariantsChange((prev) =>
                prev.map((item) =>
                  item.id === variant.id
                    ? {
                        ...item,
                        imageStatus: "generating",
                        startedAt: item.startedAt || Date.now(),
                      }
                    : item,
                ),
              );
              const knowledgePayload = {
                name: selectedKnowledge.map((item) => item.name).join(", "),
                notes: selectedKnowledge
                  .map((item) => item.notes)
                  .filter(Boolean)
                  .join("\n"),
                sourceMode: usingAnalysisMode ? "analysis" : "knowledge",
                strictKnowledgeLock: selectedKnowledge.length > 0,
                knowledgeFolders: selectedKnowledge,
                images: selectedKnowledge.flatMap((item) => item.images || []),
                referenceImages: selectedKnowledge.flatMap(
                  (item) => item.images || [],
                ),
                analysisReferenceImages,
                styleReferenceImages: pastedReferenceImages,
                tags: selectedTags,
                focusArea: formValues.focusArea || "",
                sceneMode: generationSceneModeLocal,
                creativity: formValues.creativity,
                aspectRatio: isVideoAnalysis ? videoAspectRatio : aspectRatio,
                specialInstructions: revisedSpecialInstructions,
                brand: brandLibrary,
                generationSettings: {
                  focusArea: formValues.focusArea || "",
                  sceneMode: generationSceneModeLocal,
                  creativity: formValues.creativity,
                  aspectRatio: isVideoAnalysis ? videoAspectRatio : aspectRatio,
                  promptChannel: variant.promptChannel || "",
                },
                imageModel,
                videoConfig: isVideoAnalysis
                  ? {
                      model: videoModel,
                      aspectRatio: videoAspectRatio,
                      resolution: videoResolution,
                      durationSeconds: videoDuration,
                      negativePrompt: videoNegativePrompt,
                    }
                  : null,
              };
              const mediaStart = performance.now();
              let mediaResult = null;
              let qcResult = null;
              let finalQcFailure = null;
              let retryFeedback = "";
              const attemptLimit = shouldRunQc ? maxQcRegenerations + 1 : 1;
              for (let attempt = 0; attempt < attemptLimit; attempt += 1) {
                if (isCancelled()) return;
                const attemptKnowledgePayload = {
                  ...knowledgePayload,
                  specialInstructions: retryFeedback
                    ? `${knowledgePayload.specialInstructions || ""}\nQC retry note: ${retryFeedback}`.trim()
                    : knowledgePayload.specialInstructions,
                };
                mediaResult = isVideoAnalysis
                  ? await aiService.generateVideoFromContext(
                      variant.context,
                      attemptKnowledgePayload,
                      controller.signal,
                      videoPromptOverrides.main || "",
                    )
                  : await aiService.generateImageFromContext(
                      variant.context,
                      attemptKnowledgePayload,
                      controller.signal,
                      imagePromptOverrides.main || "",
                    );
                if (!shouldRunQc) {
                  break;
                }
                if (isCancelled()) return;
                onGenerationStatusChange?.((prev) => ({
                  ...prev,
                  stage: "images",
                  message: `Running QC (${qualityControlLevel})...`,
                }));
                qcResult = await aiService.runCreativeQualityControl({
                  mediaUrl: mediaResult.videoUrl || mediaResult.imageUrl,
                  mediaType: isVideoAnalysis ? "video" : "image",
                  mimeType: mediaResult.mimeType,
                  context: variant.context,
                  qcLevel: qualityControlLevel,
                  expectedAspectRatio: knowledgePayload.aspectRatio,
                  referenceImages: selectedKnowledge.flatMap(
                    (item) => item.images || [],
                  ),
                });
                if (qcResult?.pass) {
                  finalQcFailure = null;
                  break;
                }
                finalQcFailure = qcResult;
                const canRetry = attempt < attemptLimit - 1;
                if (!canRetry) {
                  break;
                }
                const failed = Array.isArray(qcResult?.failingCriteria)
                  ? qcResult.failingCriteria.join(", ")
                  : "quality criteria";
                const aspectFailed =
                  Array.isArray(qcResult?.failingCriteria) &&
                  qcResult.failingCriteria.includes("aspect_ratio");
                const ratioSpecificRetry =
                  attemptKnowledgePayload.aspectRatio === "1:1"
                    ? "Place subject/headline/logo/CTA in the middle vertical third (center column), with background extending edge-to-edge."
                    : attemptKnowledgePayload.aspectRatio === "16:9"
                      ? "Keep all critical elements inside the middle 80% width and middle 85% height safe box."
                      : "Keep all critical elements inside a central safe area away from frame edges.";
                retryFeedback = aspectFailed
                  ? `Regenerate with the same JSON/request. Keep required aspect ratio exactly ${attemptKnowledgePayload.aspectRatio}. Recompose and resize elements to fill the frame edge-to-edge. Scale the primary subject to occupy ~60-85% of the canvas. Do NOT crop the main subject. ${ratioSpecificRetry} Do not place a wide banner inside a square canvas and do not letterbox.`
                  : `Regenerate with the same JSON/request. Fix failing criteria: ${failed}.`;
                onGenerationStatusChange?.((prev) => ({
                  ...prev,
                  stage: "images",
                  message: "Creative sent back and regenerating.",
                }));
              }
              if (shouldRunQc && finalQcFailure && !qcResult?.pass) {
                const scoreLine =
                  finalQcFailure?.scores &&
                  typeof finalQcFailure.scores === "object"
                    ? Object.entries(finalQcFailure.scores)
                        .map(
                          ([key, value]) =>
                            `${key}:${Math.round(Number(value) || 0)}`,
                        )
                        .join(", ")
                    : "";
                const issueLine =
                  finalQcFailure?.issues?.slice(0, 2)?.join("; ") ||
                  finalQcFailure?.summary ||
                  "Creative failed quality checks.";
                throw new Error(
                  `QC failed: ${issueLine}${scoreLine ? ` (scores ${scoreLine})` : ""}`,
                );
              }
              const mediaMs = performance.now() - mediaStart;
              recordTiming({
                category: isVideoAnalysis ? "videos" : "images",
                model: isVideoAnalysis ? videoModel : imageModel,
                ms: mediaMs,
                ok: true,
              });
              if (!isCancelled()) {
                onVariantsChange((prev) =>
                  prev.map((item) =>
                    item.id === variant.id
                      ? {
                          ...item,
                          imageUrl: mediaResult.imageUrl,
                          videoUrl: mediaResult.videoUrl,
                          mimeType: mediaResult.mimeType,
                          blob: mediaResult.blob || null,
                          imageStatus: "done",
                          generationModel: effectiveVariantModel,
                          imageModel: isVideoAnalysis ? videoModel : imageModel,
                          qcResult,
                          scoreStatus: isVideoAnalysis ? undefined : "scoring",
                        }
                      : item,
                  ),
                );
                onGenerationStatusChange?.((prev) => ({
                  ...prev,
                  stage: "images",
                  completed: (prev.completed || 0) + 1,
                }));
                // Auto-score generated static creatives (best-effort, async).
                if (!isVideoAnalysis) {
                  aiService
                    .scoreGeneratedCreative({
                      blob: mediaResult.blob || null,
                      dataUrl: mediaResult.imageUrl,
                      mediaType: "image",
                    })
                    .then((review) => {
                      if (isCancelled()) return;
                      onVariantsChange((prev) =>
                        prev.map((item) =>
                          item.id === variant.id
                            ? {
                                ...item,
                                creativeScore:
                                  review && Number.isFinite(Number(review.score))
                                    ? Number(review.score)
                                    : null,
                                scoreDetail: review || null,
                                scoreStatus: "done",
                              }
                            : item,
                        ),
                      );
                    })
                    .catch(() => {
                      if (isCancelled()) return;
                      onVariantsChange((prev) =>
                        prev.map((item) =>
                          item.id === variant.id
                            ? { ...item, scoreStatus: "error" }
                            : item,
                        ),
                      );
                    });
                }
              }
            } catch (err) {
              if (isCancelled() || err?.name === "AbortError") {
                return;
              }
              hadItemFailures = true;
              recordTiming({
                category: isVideoAnalysis ? "videos" : "images",
                model: isVideoAnalysis ? videoModel : imageModel,
                ms: null,
                ok: false,
              });
              recordError({
                category: isVideoAnalysis ? "videos" : "images",
                model: isVideoAnalysis ? videoModel : imageModel,
              });
              if (!isCancelled()) {
                onVariantsChange((prev) =>
                  prev.map((item) =>
                    item.id === variant.id
                      ? {
                          ...item,
                          imageStatus: "error",
                          imageError: err.message,
                          generationModel: effectiveVariantModel,
                          imageModel: isVideoAnalysis ? videoModel : imageModel,
                        }
                      : item,
                  ),
                );
                onGenerationStatusChange?.((prev) => ({
                  ...prev,
                  stage: "images",
                  completed: (prev.completed || 0) + 1,
                }));
              }
            }
          }
        })(),
      );
      await Promise.all(workers);
      if (isCancelled()) {
        runCancelled = true;
        return;
      }
      runCompleted = true;
    } catch (err) {
      if (
        err?.name === "AbortError" ||
        String(err?.message || "").includes("cancel")
      ) {
        runCancelled = true;
        messageApi.info("Generation cancelled.");
        return;
      }
      fatalErrorMessage = err?.message || "Generation failed";
      recordTiming({
        category: "variants",
        model: effectiveVariantModel,
        ms: performance.now() - variantsStart,
        ok: false,
      });
      recordError({ category: "variants", model: effectiveVariantModel });
      showErrorWithCopy(
        messageApi,
        "Generation failed",
        err,
        "VariantsControls.handleGenerate",
      );
    } finally {
      setGenerating(false);
      abortRef.current = null;
      onGenerationStatusChange?.((prev) => ({
        ...prev,
        stage: "idle",
        message: "",
      }));
      if (!runCancelled && (runCompleted || fatalErrorMessage)) {
        const hasFatalError = Boolean(fatalErrorMessage);
        const hasIssues = hasFatalError || hadItemFailures;
        const completionLabel =
          generationSourceModeLocal === "analysis"
            ? "Variant generation"
            : "Fresh creative generation";
        emitGenerationComplete({
          source: isVideoAnalysis ? "video-variants" : "image-variants",
          title: hasFatalError
            ? `${completionLabel} failed`
            : hasIssues
              ? `${completionLabel} finished with issues`
              : `${completionLabel} complete`,
          body: hasFatalError
            ? fatalErrorMessage
            : hasIssues
              ? "Some variants failed. Review the grid for details."
              : generationSourceModeLocal === "analysis"
                ? "Your variants are ready."
                : "Your fresh creatives are ready.",
          success: !hasIssues,
        });
      }
    }
  };

  const handleCancelGeneration = () => {
    if (!generating) return;
    cancelRef.current = true;
    abortRef.current?.abort();
    onVariantsChange((prev) =>
      prev.map((variant) => {
        if (variant.imageStatus === "done") return variant;
        const next = {
          ...variant,
          imageStatus: "error",
          imageError: "Cancelled",
        };
        if (Array.isArray(variant.segments)) {
          next.segments = variant.segments.map((seg) =>
            seg.status === "done"
              ? seg
              : { ...seg, status: "error", error: "Cancelled" },
          );
        }
        return next;
      }),
    );
    setGenerating(false);
    onGenerationStatusChange?.({
      stage: "idle",
      total: 0,
      completed: 0,
      message: "",
    });
    messageApi.info("Generation cancelled.");
  };

  const analysisOptions =
    history?.map((item) => ({
      value: item.id,
      label: `${item.fileName || "Creative"} • ${
        item.createdAt ? new Date(item.createdAt).toLocaleString() : "Unknown"
      }`,
    })) || [];

  const content = (
    <Space
      direction="vertical"
      size="small"
      style={{ width: "100%" }}
      className="generate-compact"
    >
      {contextHolder}
      <Segmented
        block
        value={generationSourceModeLocal}
        onChange={(value) => {
          setGenerationSourceModeLocal(value);
          onVariantsChange([]);
        }}
        options={[
          { value: "knowledge", label: "Fresh Creative" },
          {
            value: "analysis",
            label: "From Analysis",
            disabled: analysisOptions.length === 0,
          },
        ]}
      />
      {generationSourceModeLocal === "analysis" ? (
        analysisOptions.length === 0 ? (
          <Alert
            type="info"
            showIcon
            message="No analyses yet"
            description="Upload and analyze a creative first, or switch to Fresh Creative to generate directly from folders, tags, and instructions."
          />
        ) : (
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Use an uploaded creative as the source context, then branch
              variants from that analysis.
            </Text>
            <Select
              showSearch
              style={{ width: "100%" }}
              placeholder="Choose an analysis"
              optionFilterProp="label"
              options={analysisOptions}
              value={selectedId}
              disabled={disabled}
              onChange={(value) => {
                setSelectedId(value);
                const next = history?.find((item) => item.id === value);
                if (next) onSelectAnalysis?.(next);
                onVariantsChange([]);
              }}
            />
          </Space>
        )
      ) : (
        <Alert
          type="info"
          showIcon
          message="Fresh creative mode"
          description="Generate net-new image concepts from product folders, tags, brand guidance, and special instructions without using an upload analysis."
        />
      )}
      <Form
        form={genForm}
        layout="vertical"
        size="small"
        disabled={disabled}
        initialValues={{
          quantity: 3,
          creativity: 0.7,
          focusArea: "Visual Hook",
        }}
      >
        <Row gutter={[8, 8]}>
          <Col span={6}>
            <Form.Item name="quantity" label="Quantity">
              <InputNumber
                min={1}
                max={MAX_VARIANT_QUANTITY}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="creativity" label="Creativity">
              <Slider min={0} max={1} step={0.1} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="focusArea" label="Focus Area">
              <Select size="small">
                <Select.Option value="Visual Hook">Visual Hook</Select.Option>
                <Select.Option value="Messaging">Messaging</Select.Option>
                <Select.Option value="CTA">CTA</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Scene Style">
              <Segmented
                options={SCENE_MODE_OPTIONS}
                value={generationSceneModeLocal}
                onChange={setGenerationSceneModeLocal}
                block
              />
            </Form.Item>
          </Col>
          {!isVideoAnalysis && (
            <Col span={6}>
              <Form.Item label="Image Model">
                <Select
                  size="small"
                  value={imageModel}
                  onChange={setImageModel}
                  options={[
                    {
                      value: OPENAI_IMAGE_MODEL,
                      label: "GPT Image 2 (reference-aware)",
                    },
                  ]}
                />
                {kbReferenceAwareImageRouting && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Product-folder references are active, so rendering will use
                    the reference-aware image path to keep product shape and
                    branding closer to the source.
                  </Text>
                )}
              </Form.Item>
            </Col>
          )}
          {isVideoAnalysis ? (
            <>
              <Col span={24}>
                <Form.Item label="Video Model">
                  <Select
                    size="small"
                    value={videoModel}
                    onChange={setVideoModel}
                    options={[
                      {
                        value: "sora-2",
                        label: "Sora 2",
                      },
                      {
                        value: DEFAULT_VIDEO_MODEL,
                        label: "Sora 2 Pro",
                      },
                      {
                        value: "veo-3.1-generate-preview",
                        label: "Veo 3.1 Preview",
                      },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Video Aspect Ratio">
                  <Segmented
                    options={videoRatioOptions}
                    value={videoAspectRatio}
                    onChange={setVideoAspectRatio}
                    block
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Resolution">
                  <Select
                    size="small"
                    value={videoResolution}
                    onChange={setVideoResolution}
                    options={videoResolutionOptions}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                {isSegmentedVideo ? (
                  <Form.Item label="Scene Timing">
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      AI-directed per scene. Each variant is planned in parts,
                      rendered in parallel, then stitched together.
                    </Text>
                  </Form.Item>
                ) : (
                  <Form.Item label="Duration (sec)">
                    <Segmented
                      options={videoDurationOptions.map((opt) => ({
                        ...opt,
                        disabled: videoResolution !== "720p" && opt.value !== 8,
                      }))}
                      value={videoDuration}
                      onChange={setVideoDuration}
                      block
                    />
                  </Form.Item>
                )}
              </Col>
              <Col span={12}>
                <Form.Item label="Negative Prompt">
                  <Input
                    size="small"
                    placeholder="e.g. cartoon, low quality, text artifacts"
                    value={videoNegativePrompt}
                    onChange={(event) =>
                      setVideoNegativePrompt(event.target.value)
                    }
                  />
                </Form.Item>
              </Col>
            </>
          ) : (
            <Col span={24}>
              <Form.Item label="Aspect Ratio (All Models)">
                <Segmented
                  options={ratioOptions}
                  value={aspectRatio}
                  onChange={setAspectRatio}
                  block
                />
              </Form.Item>
            </Col>
          )}
          <Col span={24}>
            <Form.Item label="Product Folders">
              {knowledgeDb && knowledgeDb.length > 0 ? (
                <Select
                  mode="multiple"
                  size="small"
                  placeholder="Select product folders"
                  value={selectedKnowledgeIds}
                  options={knowledgeDb.map((item) => ({
                    value: item.id,
                    label: item.name,
                  }))}
                  onChange={setSelectedKnowledgeIds}
                />
              ) : (
                <Space>
                  <Tag color="warning">No folders</Tag>
                  <Text type="secondary">Add product folders in Settings.</Text>
                </Space>
              )}
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Tags">
              {tags && tags.length > 0 ? (
                <Select
                  mode="multiple"
                  size="small"
                  placeholder="Select tags"
                  value={selectedTagIds}
                  maxTagCount="responsive"
                  options={sortedTagOptions}
                  onChange={setSelectedTagIds}
                />
              ) : (
                <Space>
                  <Tag color="warning">No tags</Tag>
                  <Text type="secondary">Add tags in Settings.</Text>
                </Space>
              )}
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label={
                generationSceneModeLocal === "custom" ? (
                  <Space size="small">
                    <span style={{ color: "#d4881a", fontWeight: 600 }}>
                      Custom Scene Description
                    </span>
                    <span style={{ color: "#888", fontWeight: 400, fontSize: 11 }}>
                      (required — drives the scene)
                    </span>
                  </Space>
                ) : (
                  specialInstructionLabel
                )
              }
              name="specialInstructions"
            >
              <div className="special-instructions-field">
                <Input.TextArea
                  rows={2}
                  autoSize={{ minRows: 2, maxRows: 6 }}
                  onPaste={handleSpecialInstructionsPaste}
                  placeholder={
                    generationSceneModeLocal === "custom"
                      ? "Describe the exact scene you want (e.g. close-up of the face scrubber in use, clean skin energy, wet marble surface). Paste reference images here too."
                      : "Guide the next generation directly (e.g. premium studio hero, textured stone surface, no text overlay). Paste reference images here too."
                  }
                />
                <Button
                  size="small"
                  type="text"
                  icon={<UndoOutlined />}
                  className="special-instructions-undo"
                  disabled={!specialInstructionUndo}
                  onClick={handleUndoSpecialInstructions}
                />
              </div>
            </Form.Item>
            {pastedReferenceImages.length > 0 && (
              <div
                style={{
                  display: "flex",
                  gap: 6,
                  flexWrap: "wrap",
                  marginTop: -8,
                  marginBottom: 8,
                }}
              >
                {pastedReferenceImages.map((img, index) => (
                  <div
                    key={`${img.name}-${index}`}
                    style={{ position: "relative", width: 48, height: 48 }}
                  >
                    <img
                      src={img.dataUrl}
                      alt={img.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: 6,
                        border: "1px solid rgba(128, 128, 128, 0.35)",
                      }}
                    />
                    <Button
                      size="small"
                      type="primary"
                      danger
                      shape="circle"
                      icon={<CloseOutlined style={{ fontSize: 9 }} />}
                      onClick={() => handleRemovePastedReferenceImage(index)}
                      style={{
                        position: "absolute",
                        top: -6,
                        right: -6,
                        minWidth: 16,
                        width: 16,
                        height: 16,
                      }}
                    />
                  </div>
                ))}
                <Text type="secondary" style={{ fontSize: 11, alignSelf: "center" }}>
                  Sent as style references with the next generation.
                </Text>
              </div>
            )}
          </Col>
          <Col span={24}>
            <Form.Item label="Injected Text Directions">
              <Collapse
                size="small"
                defaultActiveKey={["directions"]}
                items={[
                  {
                    key: "directions",
                    label: (
                      <Space size={8} wrap>
                        <span>
                          Plain-English directions sent before the AI writes
                          variant copy
                        </span>
                        <Tag color="blue">
                          {injectedTextDirections.length} section
                          {injectedTextDirections.length === 1 ? "" : "s"}
                        </Tag>
                        {Boolean(String(variantPromptOverrides.main || "").trim()) ? (
                          <Tag color="orange">Override active</Tag>
                        ) : null}
                      </Space>
                    ),
                    children: (
                      <Space
                        direction="vertical"
                        size="small"
                        style={{ width: "100%" }}
                      >
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          This preview hides JSON and schema blocks so you can
                          inspect the human-readable guidance that gets injected
                          before the model writes the headline and supporting
                          line.
                        </Text>
                        {Boolean(String(variantPromptOverrides.main || "").trim()) ? (
                          <Alert
                            type="warning"
                            showIcon
                            message="Concept prompt override is replacing the default injected directions."
                            description="The override text below is what the model will receive for concept generation."
                          />
                        ) : null}
                        {injectedTextDirections.map((section) => (
                          <div
                            key={section.key}
                            style={{
                              borderLeft: "2px solid rgba(59, 130, 246, 0.35)",
                              paddingLeft: 12,
                            }}
                          >
                            <Text
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                textTransform: "uppercase",
                                letterSpacing: "0.06em",
                                opacity: 0.6,
                                display: "block",
                                marginBottom: 4,
                              }}
                            >
                              {section.label}
                            </Text>
                            <div
                              style={{
                                margin: 0,
                                fontSize: 12,
                                lineHeight: 1.55,
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                              }}
                            >
                              {section.value}
                            </div>
                          </div>
                        ))}
                      </Space>
                    ),
                  },
                ]}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Space style={{ width: "100%", display: "flex" }} size="small">
              <Button
                type="primary"
                icon={<RocketOutlined />}
                loading={generating}
                disabled={disabled || imageCooldownActive}
                onClick={handleGenerate}
                size="small"
                style={{ flex: 1 }}
              >
                {imageCooldownActive
                  ? `Generate Variants (${imageCooldownSeconds}s)`
                  : "Generate Variants"}
              </Button>
              {generating && (
                <Button danger size="small" onClick={handleCancelGeneration}>
                  Cancel
                </Button>
              )}
            </Space>
            <Space size={4} style={{ marginTop: 6, flexWrap: "wrap" }}>
              <PromptLibraryButton
                onClick={() => setPromptLibraryOpen(true)}
                modifiedCount={promptLibraryModifiedCount}
              />
              <PromptEditorButton
                onClick={handleOpenVariantPromptEditor}
                hasOverride={Boolean(variantPromptOverrides.main)}
                size="small"
                label="Concept Prompt"
              />
              <PromptEditorButton
                onClick={handleOpenImagePromptEditor}
                hasOverride={Boolean(imagePromptOverrides.main)}
                size="small"
                label="Image Prompt"
              />
              <PromptEditorButton
                onClick={handleOpenVideoPromptEditor}
                hasOverride={Boolean(videoPromptOverrides.main)}
                size="small"
                label="Video Prompt"
              />
            </Space>
            {imageCooldownActive ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Cooldown active for image generation: {imageCooldownSeconds}s
                remaining.
              </Text>
            ) : null}
          </Col>
        </Row>
      </Form>
    </Space>
  );

  const modals = (
    <>
      <PromptLibraryModal
        open={promptLibraryOpen}
        onClose={() => {
          setPromptLibraryOpen(false);
          setPromptLibraryModifiedCount(getOverriddenKeys().length);
        }}
      />
      <PromptEditorModal
        open={variantPromptEditorOpen}
        onClose={() => setVariantPromptEditorOpen(false)}
        title="Concept Generation Prompt — Variants"
        prompts={[{
          id: "main",
          label: "Variant Concept Prompt",
          value: variantPromptPreview || "(Select a product folder and open this dialog to preview the live prompt.)",
          description: "The prompt used to generate variant creative concepts. Includes scene mode rules, focus area, product knowledge, and special instructions.",
        }]}
        overrides={variantPromptOverrides}
        onSave={(saved) => setVariantPromptOverrides(saved)}
      />
      <PromptEditorModal
        open={imagePromptEditorOpen}
        onClose={() => setImagePromptEditorOpen(false)}
        title="Image Generation Prompt — Variants"
        prompts={[{
          id: "main",
          label: "Image Prompt",
          value: imagePromptPreview || "(Open this dialog with your current generation settings to preview the live image prompt.)",
          description: "The raw prompt that will be sent to render the next variant image. If an override is saved, that override will replace this default prompt during generation.",
        }]}
        overrides={imagePromptOverrides}
        onSave={(saved) => setImagePromptOverrides(saved)}
      />
      <PromptEditorModal
        open={videoPromptEditorOpen}
        onClose={() => setVideoPromptEditorOpen(false)}
        title="Video Generation Prompt — Variants"
        prompts={[{
          id: "main",
          label: "Video Prompt",
          value: videoPromptPreview || "(Open this dialog with your current generation settings to preview the live video prompt.)",
          description: "The raw prompt that will be sent to render the next variant video scene. If an override is saved, that override will replace this default prompt during generation.",
        }]}
        overrides={videoPromptOverrides}
        onSave={(saved) => setVideoPromptOverrides(saved)}
      />
    </>
  );

  if (!showCard) {
    return <>{content}{modals}</>;
  }

  return (
    <>
      <Card size="small" title="Generation Settings">
        {content}
      </Card>
      {modals}
    </>
  );
}

export default VariantsControls;
